<script setup lang="ts">
import { ref, computed } from 'vue';

const props = withDefaults(
  defineProps<{
    customHeaderClasses?: string;
    glueIconToTitle?: boolean;
    hasCustomAction?: boolean;
    hasDelete?: boolean;
    hasEdit?: boolean;
    header?: string;
    headerLink?: string;
    headerTooltip?: string;
    headless?: boolean;
    loading?: boolean;
    maxHeight?: boolean | string;
    noBorder?: boolean;
    noPadding?: boolean;
    stickyOnEditMode?: boolean;
    toggleCollapse?: boolean;
    toggleable?: boolean;
  }>(),
  {
    headless: false,
    stickyOnEditMode: true,
    toggleable: true,
  }
);

const emits = defineEmits<{
  onCancel: [];
  onDelete: [];
  onToggleEditMode: [];
  onUpdate: [];
}>();

const collapsed = defineModel('collapsed', { default: true });
const isOnEditMode = defineModel('isOnEditMode', { default: false });
const panelRef = ref();

const maxHeightClass = computed(() => {
  if (typeof props.maxHeight === 'string') return `max-h-[${props.maxHeight}] overflow-y-auto`;

  return props.maxHeight ? 'max-h-[400px] overflow-y-auto' : '';
});

function onEdit() {
  const isPanelCollapsed = panelRef.value.d_collapsed;
  if (isPanelCollapsed) panelRef.value.toggle();

  if (isOnEditMode.value) return emits('onUpdate');

  isOnEditMode.value = true;
}

function onToggle() {
  collapsed.value = !collapsed.value;
}
</script>

<template>
  <Panel
    ref="panelRef"
    pt:toggler="af-filter-button h-9 w-9 rounded border-none"
    :collapsed="toggleCollapse"
    :toggleable="!headless && !loading && toggleable"
    :pt:content:class="{ 'px-0': noPadding, [maxHeightClass]: maxHeight }"
    :class="[
      {
        '!border-0 bg-gray-100 shadow': isOnEditMode,
        'pt-[18px]': headless,
        'border-none': noBorder,
      },
    ]"
    :pt:header:class="{
      hidden: headless,
      'bg-gray-100 top-0 px-5 py-3 sticky z-10': stickyOnEditMode && isOnEditMode,
      'px-0 pt-0': noPadding,
      'border-none': noBorder,
      'justify-start': glueIconToTitle,
    }"
    :pt:icons="['flex']"
    @toggle="onToggle()"
  >
    <template #togglericon>
      <i
        v-if="collapsed"
        class="pi pi-chevron-up"
      />

      <i
        v-else
        class="pi pi-chevron-down"
      />
    </template>

    <template
      v-if="!headless"
      #header
    >
      <div
        :class="[
          'flex items-center',
          customHeaderClasses,
          hasCustomAction ? 'mr-10 overflow-hidden text-ellipsis whitespace-nowrap' : '',
        ]"
      >
        <slot name="customHeader" />

        <RouterLink
          v-if="headerLink"
          :to="headerLink"
        >
          <span class="af-link text-base font-semibold">
            {{ header }}
          </span>
        </RouterLink>

        <span
          v-else
          v-tooltip.top="{ value: headerTooltip ? headerTooltip : '', showDelay: 1000 }"
          :class="['text-base font-semibold', hasCustomAction ? 'overflow-hidden text-ellipsis whitespace-nowrap' : '']"
        >
          {{ header }}
        </span>

        <i
          v-if="loading"
          class="pi pi-spin pi-spinner ml-2 text-[1.087rem] text-gray-600"
        />
      </div>
    </template>

    <!-- TODO :: [AF-214] Convert buttons into molecules -->
    <template
      v-if="!headless"
      #icons
    >
      <slot
        v-if="$slots.customActions"
        name="customActions"
      />

      <Skeleton
        v-if="loading && hasEdit"
        class="rounded-3"
        height="36px"
        width="36px"
      />

      <Button
        v-else-if="hasEdit"
        outlined
        :icon="isOnEditMode ? 'pi pi-save' : 'pi pi-pencil'"
        :class="['af-filter-button mr-1 h-8 w-9 border-none', { '!text-brand-500': isOnEditMode }]"
        @click="onEdit"
      />

      <Skeleton
        v-if="loading && hasEdit && isOnEditMode"
        class="rounded-3"
        height="36px"
        width="36px"
      />

      <Button
        v-else-if="hasEdit && isOnEditMode"
        outlined
        class="af-filter-button h-8 w-9 border-none"
        icon="pi pi-times"
        severity="danger"
        @click="emits('onCancel')"
      />

      <Skeleton
        v-if="loading && hasDelete"
        class="rounded-3"
        height="36px"
        width="36px"
      />

      <Button
        v-else-if="hasDelete"
        outlined
        class="af-filter-button mr-1 h-8 w-9 border-none"
        icon="pi pi-trash"
        severity="danger"
        @click="emits('onDelete')"
      />
    </template>

    <slot />

    <template
      v-if="$slots.footer"
      #footer
    >
      <slot name="footer" />
    </template>
  </Panel>
</template>
